import type { CSSProperties } from "react";
import { LitFilledButton, LitFilledTonalButton, LitOutlinedButton } from "@/components/Button/LitButton";
import GlobalToolbarIconButton from "@/components/Button/GlobalToolbarIconButton";
import { SlotIcon, type SlotIconName } from "@/components/Icon";
import Menu, { type MenuListEntry } from "@/components/Menu";
import KeyboardShortcutHint from "@/components/KeyboardShortcutHint";
import { sysColors } from "@/assets/theme/colors";
import classNames from "classnames";
import { globalToolbarIconButtonWrapClassName } from "@/components/Button/globalAddButtonFilledStyle";
import { compactGlobalNavbarIconBoxClassName } from "@/components/TopActionBar/compactNavbarChrome";

export type QuickActionMenuButtonProps = {
  actions: MenuListEntry[];
  testId?: string;
  label: string;
  menuTitle?: string;
  menuClassName?: string;
  triggerVariant?: "outlined" | "filledTonal" | "filled";
  triggerIcon?: SlotIconName;
  iconOnly?: boolean;
  menuOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  /** Shows `a` beside the trigger (global Add menu). */
  showOpenShortcutHint?: boolean;
};

const OUTLINED_TRIGGER_STYLE: CSSProperties = {
  gap: "4px",
  "--md-outlined-button-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-focus-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-pressed-label-text-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-focus-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-pressed-icon-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-state-layer-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-hover-state-layer-opacity": "0.08",
  "--md-outlined-button-pressed-state-layer-color": sysColors["sys-brand-on-surface"],
  "--md-outlined-button-pressed-state-layer-opacity": "0.12"
} as CSSProperties;

const TRIGGER_COMPONENTS = {
  outlined: LitOutlinedButton,
  filledTonal: LitFilledTonalButton,
  filled: LitFilledButton
} as const;

function MenuTriggerButton({
  triggerVariant,
  testId,
  label,
  triggerIcon
}: {
  triggerVariant: keyof typeof TRIGGER_COMPONENTS;
  testId?: string;
  label: string;
  triggerIcon?: SlotIconName;
}) {
  const TriggerComponent = TRIGGER_COMPONENTS[triggerVariant];
  const style = triggerVariant === "outlined" ? OUTLINED_TRIGGER_STYLE : { gap: "4px" };

  if (triggerIcon) {
    return (
      <TriggerComponent data-testid={testId} type="button" style={style}>
        <SlotIcon name={triggerIcon} slot="icon" size={18} />
        {label}
      </TriggerComponent>
    );
  }

  return (
    <TriggerComponent data-testid={testId} type="button" style={style} trailingIcon>
      {label}
      <SlotIcon name="expand_more" slot="icon" size={18} />
    </TriggerComponent>
  );
}

export default function QuickActionMenuButton({
  actions,
  testId,
  label,
  menuTitle,
  menuClassName,
  triggerVariant = "outlined",
  triggerIcon,
  iconOnly = false,
  menuOpen,
  onMenuOpenChange,
  showOpenShortcutHint = false
}: QuickActionMenuButtonProps) {
  const trigger = iconOnly ? (
    <GlobalToolbarIconButton
      icon={triggerIcon ?? "add"}
      ariaLabel={label}
      testId={testId}
      ariaKeyShortcuts={showOpenShortcutHint ? "a" : undefined}
    />
  ) : (
    <MenuTriggerButton triggerVariant={triggerVariant} testId={testId} label={label} triggerIcon={triggerIcon} />
  );

  const menu = (
    <Menu
      menuItems={actions}
      AnchorComponent={trigger}
      title={menuTitle}
      menuClassName={menuClassName}
      open={menuOpen}
      onOpenChange={onMenuOpenChange}
    />
  );

  if (!showOpenShortcutHint) {
    return menu;
  }

  return (
    <span
      className={classNames(
        globalToolbarIconButtonWrapClassName,
        compactGlobalNavbarIconBoxClassName,
        iconOnly ? "gap-1" : "flex items-center gap-1"
      )}
    >
      {menu}
      <KeyboardShortcutHint keyLabel="a" showModifier={false} testId="global-add-menu-shortcut-hint" />
    </span>
  );
}
